// import library
import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CryptoJS from 'crypto-js';
import axios from "axios";
// import files
import { env } from "../env";
import i18n from '../i18n/config';

export const AuthContext = createContext({
    userInfo: null,
    isAuthenticated: false,
    token: null,
    loginPageLogo: null,
    loginPageBackgroundImage: null,
    companyWebsite: null,
    isCompanyLoading: true,
    aiChatBaseUrl: "",
    aiChatAuth: "",
    login: () => {},
    logout: () => {},
});

const AuthContextProvider = ({ children }) => {
    // variables
    const navigate = useNavigate();
    const location = useLocation();
    const [userInfo, setUserInfo] = useState(null);
    const [isAuthenticated, setisAuthenticated] = useState(false);
    const [loginPageLogo, setLoginPageLogo] = useState("");
    const [loginPageBackgroundImage, setLoginPageBackgroundImage] = useState();
    const [companyWebsite, setCompanyWebsite] = useState("");
    const [isCompanyLoading, setIsCompanyLoading] = useState(!localStorage.getItem('userInfo'));
    const [aiChatBaseUrl, setAiChatBaseUrl] = useState("");
    const [aiChatAuth, setAiChatAuth] = useState("");
    const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
    const baseUrl = env.REACT_APP_API_URL;
    const encrypt = localStorage.getItem('userInfo');
    const token = localStorage.getItem('token');

    // functions
    useEffect(() => {
        const fetchData = async () => {
            if (encrypt) {
                const stored = decryptData(encrypt);
                if (stored?.companyInfo) {
                    setLoginPageLogo(stored.companyInfo.adminLoginPageLogo);
                    setLoginPageBackgroundImage(stored.companyInfo.adminLoginPageBackgroundImage);
                    setCompanyWebsite(stored.companyInfo.website);
                    setIsCompanyLoading(false);
                    return;
                }
            }
            await axios.get(baseUrl+`/api/Company/GetCompanyInfoDetails?companyid=${env.REACT_APP_COMPANY_ID}`, { headers: {
                "Accept" : "Application/json",
                "Content-Type" : "Application/json"
            }}).then((response) => {
                if(response.data.statusCode === 200) {
                    setLoginPageLogo(response.data.data.adminLoginPageLogo);
                    setLoginPageBackgroundImage(response.data.data.adminLoginPageBackgroundImage);
                    setCompanyWebsite(response.data.data.website);
                }
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                setIsCompanyLoading(false);
            });
        };
        fetchData();
        
        // token check
        if(!token) {
            removeItem();
            return;
        }
        // data check
        if(!encrypt) {
            removeItem();
            return;
        } else {
            const decrypt = decryptData(encrypt);
            if (!decrypt) return;

            // check for expiry date
            const currentDate = Math.floor(Date.now() /1000);
            const expiryDate = decrypt.expiryDate;
            if(expiryDate) {
                if (expiryDate < currentDate) {
                    removeItem();
                    localStorage.setItem('remember', 0);
                    return;
                }
            } else {
                removeItem();
                localStorage.setItem('remember', 0);
                return;
            }
            setUserInfo(decrypt);
            setisAuthenticated(true);
            fetchAiConfig(token);
            navigate(location.pathname);
        }
    }, [isAuthenticated]);

    const removeItem = () => {
        if(location.pathname === '/' || location.pathname === '/forgot-password' || location.pathname === '/server-setting' || location.pathname === '/reset-password') {
            navigate(location.pathname);
        } else {
            navigate('/');
        }
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");
        localStorage.removeItem("defaultLanguage");
        localStorage.removeItem("locale");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem('co_tab');
        localStorage.removeItem('co_coupon');
        localStorage.removeItem('co_comment');
        localStorage.removeItem('co_method');
        localStorage.removeItem('co_id');
        localStorage.removeItem('co_quantity');
        localStorage.removeItem('co_qty_message');
        localStorage.removeItem('co_items');
        localStorage.removeItem('co_calculate');
        localStorage.removeItem('co_code');
        localStorage.removeItem('co_calculate_json');
        localStorage.removeItem('co_calculate_button');
        localStorage.removeItem('co_calculate_filter');
        localStorage.removeItem('profile_tab');
        setUserInfo(null);
        setisAuthenticated(false);
    };
    const encryptData = (data) => {
        try {
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
            return encrypted;
        } catch (e) {
            removeItem();
        }
    };
    const decryptData = (data) => {
        try {
            const decrypted = CryptoJS.AES.decrypt(data, SECRET_KEY).toString(CryptoJS.enc.Utf8);
            return JSON.parse(decrypted);
        } catch (e) {
            removeItem();
            window.location.reload();
        }
    };
    const fetchAiConfig = async (authToken) => {
        try {
            const response = await axios.get(`${baseUrl}/api/OfflineSync/GetCompanyErpRelationsList`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            const config = response.data?.[0];
            if (config?.AI_CHAT_BASE_URL) setAiChatBaseUrl(config.AI_CHAT_BASE_URL);
            if (config?.AI_CHAT_AUTH) setAiChatAuth(config.AI_CHAT_AUTH);
        } catch (error) {
            console.error("[fetchAiConfig] Failed:", error?.response?.status, error?.message);
        }
    };

    const fetchTranslations = async (language, headers) => {
        const response = await axios.get(
          `${baseUrl}/api/SystemTranslation/GetEzaTranslationKeyValue?language=${language}`,
          { headers }
        );
      
        const translations = {};
        for (const item of response.data) {
          translations[item.key.toLowerCase()] = item.value || item.defaultvalue;
        }
      
        return translations;
    };
    const login = async (newToken, userData) => {
        localStorage.setItem("token", newToken);
        fetchAiConfig(newToken);
        
        await axios.post(baseUrl + "/api/User/GetUserDetailsByUserID", { "UserName": userData.userName }, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${newToken}`,
            },
        }).then( async (response) => {
            const supportedLangs = ['en', 'gr'];
            const defaultLang = response.data.defaultLanguage;

            const newLanguage = supportedLangs.includes(defaultLang)
            ? defaultLang
            : supportedLangs[0];

            i18n.changeLanguage(newLanguage);
            localStorage.setItem('locale', newLanguage);
            localStorage.setItem('defaultLanguage', 1);

            if (response.data.isAdministrator) {
                localStorage.setItem('isAdmin', 1);
            } else {
                localStorage.setItem('isAdmin', 0);
            }

            const headers = {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${newToken}`,
            }
      
            const [english, greek] = await Promise.all([
                fetchTranslations('en', headers),
                fetchTranslations('gr', headers),
            ]);

            i18n.addResourceBundle('en', 'translations', english, true, true);
            i18n.addResourceBundle('gr', 'translations', greek, true, true);
        }).catch((error) => {
            console.error("Error fetching user details:", error);
        });

        // Encrypt and store user data.
        const encryptedUserData = encryptData(userData);
        localStorage.setItem('userInfo', encryptedUserData);
      
        // Update local state.
        setUserInfo(decryptData(encryptedUserData));
        setisAuthenticated(true);
      
        // Navigate to the orders page.
        navigate("/orders");
    };
    const logout = () => {
        removeItem();
        navigate("/");
    };

    return (
        <AuthContext.Provider value={{userInfo, setUserInfo, isAuthenticated, loginPageLogo, loginPageBackgroundImage, companyWebsite, isCompanyLoading, aiChatBaseUrl, aiChatAuth, setisAuthenticated, login, logout}}>
            {children}
        </AuthContext.Provider>
    );
};

export  {
    AuthContextProvider
};